import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { GroupOrUnknownDisplayName } from "@/components/display/GroupDisplayName";
import { UserDisplayName } from "@/components/display/UserDisplayName";
import { TableSkeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useGrantsData } from "@/data/use-grants-data";
import { useGroupExpertsData } from "@/data/use-group-experts-data";
import { useGroupData } from "@/data/use-groups-data";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

// ============================================================
// Expert Row
// ============================================================

const ExpertRow = ({
  expert,
  groupUuid,
}: {
  expert: {
    uuid: string;
    name: string;
    description: string;
    user: { name: string } | null;
    group: { name: string } | null;
  };
  groupUuid: string;
}) => {
  const { grants, upsert, revoke } = useGrantsData({
    expertUuid: expert.uuid,
  });
  const [isPending, setIsPending] = useState(false);
  const isEnabled = grants.some((grant) => grant.group.uuid === groupUuid);

  const handleToggle = async (checked: boolean) => {
    setIsPending(true);
    try {
      if (checked) {
        await upsert({ groupUuid, access: "write" });
      } else {
        await revoke({ groupUuid });
      }
    } catch {
      toast.error("Failed to update expert access.");
    } finally {
      setIsPending(false);
    }
  };

  return (
    <TableRow>
      <TableCell className="font-medium">{expert.name}</TableCell>
      <TableCell className="text-muted-foreground">
        {expert.description}
      </TableCell>
      <TableCell>
        {expert.user ? <UserDisplayName user={expert.user} /> : "—"}
      </TableCell>
      <TableCell>
        <GroupOrUnknownDisplayName group={expert.group} />
      </TableCell>
      <TableCell>
        <Switch
          checked={isEnabled}
          disabled={isPending}
          onCheckedChange={handleToggle}
        />
      </TableCell>
    </TableRow>
  );
};

// ============================================================
// Page
// ============================================================

export const GlobalAdminGroupExpertsPage = () => {
  const { groupUuid } = useParams({ strict: false }) as {
    groupUuid?: string;
  };
  const { group } = useGroupData(groupUuid);
  const parentGroupUuid = group?.parentGroupUuid ?? null;
  const { group: parentGroup } = useGroupData(parentGroupUuid);
  const { experts: parentExperts, isLoading } =
    useGroupExpertsData(parentGroupUuid);

  if (!group) return null;

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Experts</PageHeader.Title>
      </PageHeader>
      <PageBody>
        {!parentGroupUuid && (
          <p className="text-muted-foreground text-sm">
            This group has no parent group. There are no parent artifacts to
            manage.
          </p>
        )}
        {parentGroupUuid && (
          <p className="text-muted-foreground text-sm">
            These experts are originally shared or published in the parent group{" "}
            <span className="text-foreground font-medium">
              {parentGroup?.name ?? parentGroupUuid}
            </span>
            .
          </p>
        )}
        {parentGroupUuid && isLoading && <TableSkeleton />}
        {parentGroupUuid && !isLoading && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Author</TableHead>
                <TableHead>Group</TableHead>
                <TableHead>Access</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {parentExperts.map((expert) => (
                <ExpertRow
                  key={expert.uuid}
                  expert={expert}
                  groupUuid={groupUuid!}
                />
              ))}
              {parentExperts.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-muted-foreground py-4 text-center"
                  >
                    No experts in the parent group.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </PageBody>
    </Page>
  );
};
